export type ReviewStatus =
  | 'draft'
  | 'sent'
  | 'in_review'
  | 'approved'
  | 'changes_requested'
export type ReviewItemStatus = 'pending' | 'approved' | 'rejected'
export type MatchSource =
  | 'exact_sku'
  | 'manufacturer_match'
  | 'manual_match'
  | 'new_entry'

export const matchSourceConfig: Record<
  MatchSource,
  { label: string; color: string; bgColor: string; borderColor: string }
> = {
  exact_sku: {
    label: 'SKU Match',
    color: 'text-emerald-700',
    bgColor: 'bg-emerald-50',
    borderColor: 'border-emerald-200',
  },
  manufacturer_match: {
    label: 'Mfr Match',
    color: 'text-blue-700',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200',
  },
  manual_match: {
    label: 'Manual Match',
    color: 'text-amber-700',
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-200',
  },
  new_entry: {
    label: 'New Entry',
    color: 'text-purple-700',
    bgColor: 'bg-purple-50',
    borderColor: 'border-purple-200',
  },
}

export type ReviewItem = {
  id: string
  optionName: string
  description: string
  category: string
  subcategory: string
  variantSet: string
  price: number
  isStandard: boolean
  imageUrl?: string
  hasImage: boolean
  matchSource: MatchSource
  status: ReviewItemStatus
  feedback?: string
  manufacturerName?: string
  sku?: string
}

export type ReviewPackage = {
  id: string
  builderId: string
  builderName: string
  catalogName: string
  status: ReviewStatus
  createdAt: string
  sentAt?: string
  dueDate?: string
  submittedAt?: string
  notes?: string
  items: ReviewItem[]
}

export const initialReviewPackages: ReviewPackage[] = [
  {
    id: 'review-1',
    builderId: 'mattamy',
    builderName: 'Mattamy Homes',
    catalogName: 'Interior Options — Summit Ridge',
    status: 'in_review',
    createdAt: '2026-02-10',
    sentAt: '2026-02-12',
    dueDate: '2026-02-25',
    notes:
      'Please review all interior options for the Summit Ridge community. Pay special attention to the kitchen appliance packages — we matched these to your spec sheet.',
    items: [
      // ── Appliances ──
      {
        id: 'ri-1',
        optionName: 'Stainless Steel Appliance Package',
        description:
          'Premium stainless steel appliance package including fridge, stove, dishwasher, and microwave. GE Profile series.',
        category: 'Interior',
        subcategory: 'Appliances',
        variantSet: 'Appliance Package',
        price: 2500,
        isStandard: false,
        hasImage: true,
        imageUrl:
          'https://cdn.magicpatterns.com/uploads/uz3yKu22Y2YDi2VtcShnwy/stainless-steel-fridge.jpg',
        matchSource: 'exact_sku',
        status: 'pending',
        manufacturerName: 'GE Profile',
        sku: 'GE-PFE28K',
      },
      {
        id: 'ri-2',
        optionName: 'Black + White Appliance Package',
        description:
          'Standard black and white appliance package — included in base price. GE standard series.',
        category: 'Interior',
        subcategory: 'Appliances',
        variantSet: 'Appliance Package',
        price: 0,
        isStandard: true,
        hasImage: true,
        imageUrl:
          'https://images.unsplash.com/photo-1584568694244-14fbdf83bd30?auto=format&fit=crop&w=400&q=80',
        matchSource: 'manufacturer_match',
        status: 'pending',
        manufacturerName: 'GE',
        sku: 'GE-STD-BW',
      },
      {
        id: 'ri-3',
        optionName: 'Built-in Appliance Package',
        description:
          'Premium built-in appliance package with panel-ready fronts. Sub-Zero / Wolf series.',
        category: 'Interior',
        subcategory: 'Appliances',
        variantSet: 'Appliance Package',
        price: 8500,
        isStandard: false,
        hasImage: true,
        imageUrl:
          'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?auto=format&fit=crop&w=400&q=80',
        matchSource: 'manual_match',
        status: 'pending',
        manufacturerName: 'Sub-Zero / Wolf',
      },

      // ── Cabinets ──
      {
        id: 'ri-13',
        optionName: 'Shaker-Style Painted Cabinets',
        description:
          'Full overlay shaker-style cabinets in painted white finish. Soft-close hinges and drawers. KraftMaid Vantage series.',
        category: 'Interior',
        subcategory: 'Cabinets',
        variantSet: 'Kitchen Cabinets',
        price: 0,
        isStandard: true,
        hasImage: true,
        imageUrl:
          'https://images.unsplash.com/photo-1556909114-44e3e70034e2?auto=format&fit=crop&w=400&q=80',
        matchSource: 'exact_sku',
        status: 'pending',
        manufacturerName: 'KraftMaid',
        sku: 'KM-VAN-SHK-WHT',
      },
      {
        id: 'ri-14',
        optionName: 'Flat-Panel Modern Cabinets',
        description:
          'Slab-front flat-panel cabinets in matte grey. European-style frameless construction.',
        category: 'Interior',
        subcategory: 'Cabinets',
        variantSet: 'Kitchen Cabinets',
        price: 3200,
        isStandard: false,
        hasImage: true,
        imageUrl:
          'https://images.unsplash.com/photo-1600585152220-90363fe7e115?auto=format&fit=crop&w=400&q=80',
        matchSource: 'manufacturer_match',
        status: 'pending',
        manufacturerName: 'KraftMaid',
        sku: 'KM-VAN-FP-GRY',
      },
      {
        id: 'ri-15',
        optionName: 'Stained Oak Cabinets',
        description:
          'Natural stained oak cabinets with raised panel doors. Traditional style with crown molding option.',
        category: 'Interior',
        subcategory: 'Cabinets',
        variantSet: 'Kitchen Cabinets',
        price: 4800,
        isStandard: false,
        hasImage: true,
        imageUrl:
          'https://images.unsplash.com/photo-1604709177225-055f99402ea3?auto=format&fit=crop&w=400&q=80',
        matchSource: 'new_entry',
        status: 'pending',
      },
      {
        id: 'ri-16',
        optionName: 'Bathroom Vanity — 48" Double',
        description:
          '48-inch double vanity with quartz top and undermount sinks. Painted white finish.',
        category: 'Interior',
        subcategory: 'Cabinets',
        variantSet: 'Bathroom Vanities',
        price: 2200,
        isStandard: false,
        hasImage: true,
        imageUrl:
          'https://images.unsplash.com/photo-1620626011761-996317b8d101?auto=format&fit=crop&w=400&q=80',
        matchSource: 'manual_match',
        status: 'pending',
        manufacturerName: 'KraftMaid',
      },

      // ── Countertops ──
      {
        id: 'ri-4',
        optionName: 'White Quartz Countertops',
        description:
          'Calacatta-style white quartz countertops with subtle grey veining. Caesarstone 5143 White Attica.',
        category: 'Interior',
        subcategory: 'Countertops',
        variantSet: 'Kitchen Countertops',
        price: 4500,
        isStandard: false,
        hasImage: true,
        imageUrl:
          'https://images.unsplash.com/photo-1600566752355-35792bedcfea?auto=format&fit=crop&w=400&q=80',
        matchSource: 'exact_sku',
        status: 'pending',
        manufacturerName: 'Caesarstone',
        sku: 'CS-5143',
      },
      {
        id: 'ri-5',
        optionName: 'Laminate Countertops',
        description:
          'Standard laminate countertops in Wilsonart HD series. Multiple colors available.',
        category: 'Interior',
        subcategory: 'Countertops',
        variantSet: 'Kitchen Countertops',
        price: 0,
        isStandard: true,
        hasImage: true,
        imageUrl:
          'https://images.unsplash.com/photo-1556909172-54557c7e4fb7?auto=format&fit=crop&w=400&q=80',
        matchSource: 'manufacturer_match',
        status: 'pending',
        manufacturerName: 'Wilsonart',
      },
      {
        id: 'ri-17',
        optionName: 'Granite Countertops — Level 3',
        description:
          'Premium granite countertops with eased edge profile. Multiple color options from MSI.',
        category: 'Interior',
        subcategory: 'Countertops',
        variantSet: 'Kitchen Countertops',
        price: 5200,
        isStandard: false,
        hasImage: true,
        imageUrl:
          'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?auto=format&fit=crop&w=400&q=80',
        matchSource: 'manufacturer_match',
        status: 'pending',
        manufacturerName: 'MSI',
        sku: 'MSI-GRN-L3',
      },
      {
        id: 'ri-18',
        optionName: 'Bathroom Quartz Countertops',
        description:
          'Quartz countertops for all bathroom vanities. Matches kitchen quartz selection.',
        category: 'Interior',
        subcategory: 'Countertops',
        variantSet: 'Bathroom Countertops',
        price: 1800,
        isStandard: false,
        hasImage: true,
        imageUrl:
          'https://images.unsplash.com/photo-1552321554-5fefe8c9ef14?auto=format&fit=crop&w=400&q=80',
        matchSource: 'exact_sku',
        status: 'pending',
        manufacturerName: 'Caesarstone',
        sku: 'CS-5143-BTH',
      },

      // ── Plumbing Fixtures ──
      {
        id: 'ri-11',
        optionName: 'Pot Filler Faucet',
        description:
          'Wall-mounted pot filler faucet in brushed nickel. Delta Contemporary series.',
        category: 'Interior',
        subcategory: 'Plumbing Fixtures',
        variantSet: 'Kitchen Fixtures',
        price: 950,
        isStandard: false,
        hasImage: true,
        imageUrl:
          'https://images.unsplash.com/photo-1585704032915-c3400ca199e7?auto=format&fit=crop&w=400&q=80',
        matchSource: 'new_entry',
        status: 'pending',
      },
      {
        id: 'ri-19',
        optionName: 'Kitchen Faucet — Touchless',
        description:
          'Touchless pull-down kitchen faucet in brushed nickel. Moen Arbor MotionSense.',
        category: 'Interior',
        subcategory: 'Plumbing Fixtures',
        variantSet: 'Kitchen Fixtures',
        price: 450,
        isStandard: false,
        hasImage: true,
        imageUrl:
          'https://images.unsplash.com/photo-1584622650111-993a426fbf0a?auto=format&fit=crop&w=400&q=80',
        matchSource: 'exact_sku',
        status: 'pending',
        manufacturerName: 'Moen',
        sku: 'MOEN-7594ESRS',
      },
      {
        id: 'ri-20',
        optionName: 'Standard Kitchen Faucet',
        description:
          'Single-handle pull-down kitchen faucet in chrome. Delta Essa series. Included in base.',
        category: 'Interior',
        subcategory: 'Plumbing Fixtures',
        variantSet: 'Kitchen Fixtures',
        price: 0,
        isStandard: true,
        hasImage: true,
        imageUrl:
          'https://images.unsplash.com/photo-1585351650024-3a348e1b82d1?auto=format&fit=crop&w=400&q=80',
        matchSource: 'manufacturer_match',
        status: 'pending',
        manufacturerName: 'Delta',
        sku: 'DLT-9113-DST',
      },
      {
        id: 'ri-21',
        optionName: 'Freestanding Soaker Tub',
        description:
          '67-inch freestanding acrylic soaker tub. Modern oval design with chrome drain.',
        category: 'Interior',
        subcategory: 'Plumbing Fixtures',
        variantSet: 'Bathroom Fixtures',
        price: 3200,
        isStandard: false,
        hasImage: true,
        imageUrl:
          'https://images.unsplash.com/photo-1507652313519-d4e9174996dd?auto=format&fit=crop&w=400&q=80',
        matchSource: 'manual_match',
        status: 'pending',
        manufacturerName: 'Kohler',
        sku: 'KOH-K-24002',
      },
      {
        id: 'ri-22',
        optionName: 'Frameless Glass Shower Enclosure',
        description:
          '3/8" frameless glass shower enclosure with brushed nickel hardware. Custom fit to shower opening.',
        category: 'Interior',
        subcategory: 'Plumbing Fixtures',
        variantSet: 'Bathroom Fixtures',
        price: 2800,
        isStandard: false,
        hasImage: true,
        imageUrl:
          'https://images.unsplash.com/photo-1620626011761-996317b8d101?auto=format&fit=crop&w=400&q=80',
        matchSource: 'new_entry',
        status: 'pending',
      },

      // ── Flooring ──
      {
        id: 'ri-6',
        optionName: 'Natural Oak Hardwood',
        description:
          'Classic natural oak hardwood flooring, 5" wide plank. Mirage Natural collection.',
        category: 'Interior',
        subcategory: 'Flooring',
        variantSet: 'Hardwood',
        price: 8500,
        isStandard: false,
        hasImage: true,
        imageUrl:
          'https://images.unsplash.com/photo-1558618666-fcd25c85f82e?auto=format&fit=crop&w=400&q=80',
        matchSource: 'exact_sku',
        status: 'pending',
        manufacturerName: 'Mirage',
        sku: 'MRG-NAT-OAK-5',
      },
      {
        id: 'ri-7',
        optionName: 'Engineered Maple Hardwood',
        description:
          'Engineered maple hardwood in a warm honey tone. 6" wide plank format.',
        category: 'Interior',
        subcategory: 'Flooring',
        variantSet: 'Hardwood',
        price: 6200,
        isStandard: false,
        hasImage: true,
        imageUrl:
          'https://images.unsplash.com/photo-1615873968403-89e068629265?auto=format&fit=crop&w=400&q=80',
        matchSource: 'new_entry',
        status: 'pending',
      },
      {
        id: 'ri-8',
        optionName: 'Luxury Vinyl Plank — Oak Look',
        description:
          'Waterproof luxury vinyl plank with realistic oak texture. COREtec Plus Enhanced series.',
        category: 'Interior',
        subcategory: 'Flooring',
        variantSet: 'LVP',
        price: 3800,
        isStandard: false,
        hasImage: true,
        imageUrl:
          'https://images.unsplash.com/photo-1622398925373-3f91b1e275f5?auto=format&fit=crop&w=400&q=80',
        matchSource: 'manufacturer_match',
        status: 'pending',
        manufacturerName: 'COREtec',
        sku: 'CT-PLUS-OAK',
      },
      {
        id: 'ri-9',
        optionName: 'Standard Carpet — Plush',
        description:
          'Standard plush carpet in neutral tones. Shaw Floors Caress collection. Included in base.',
        category: 'Interior',
        subcategory: 'Flooring',
        variantSet: 'Carpet',
        price: 0,
        isStandard: true,
        hasImage: true,
        imageUrl:
          'https://images.unsplash.com/photo-1558618666-fcd25c85f82e?auto=format&fit=crop&w=400&q=80',
        matchSource: 'exact_sku',
        status: 'pending',
        manufacturerName: 'Shaw Floors',
        sku: 'SF-CARESS-PLH',
      },
      {
        id: 'ri-10',
        optionName: 'Upgraded Carpet — Patterned',
        description:
          'Premium patterned carpet with stain resistance. Shaw Floors Caress Deluxe.',
        category: 'Interior',
        subcategory: 'Flooring',
        variantSet: 'Carpet',
        price: 1800,
        isStandard: false,
        hasImage: false,
        matchSource: 'manual_match',
        status: 'pending',
        manufacturerName: 'Shaw Floors',
      },

      // ── Lighting & Electrical ──
      {
        id: 'ri-12',
        optionName: 'Under-Cabinet Lighting',
        description:
          'LED under-cabinet lighting package for kitchen. Warm white, dimmable.',
        category: 'Interior',
        subcategory: 'Lighting & Electrical',
        variantSet: 'Kitchen Lighting',
        price: 650,
        isStandard: false,
        hasImage: true,
        imageUrl:
          'https://images.unsplash.com/photo-1565538810643-b5bdb714032a?auto=format&fit=crop&w=400&q=80',
        matchSource: 'new_entry',
        status: 'pending',
      },
      {
        id: 'ri-23',
        optionName: 'Recessed Lighting Package',
        description:
          '6-inch LED recessed lights throughout main floor. Includes 12 fixtures with dimmer switches.',
        category: 'Interior',
        subcategory: 'Lighting & Electrical',
        variantSet: 'General Lighting',
        price: 1200,
        isStandard: false,
        hasImage: true,
        imageUrl:
          'https://images.unsplash.com/photo-1524484485831-a92ffc0de03f?auto=format&fit=crop&w=400&q=80',
        matchSource: 'manufacturer_match',
        status: 'pending',
        manufacturerName: 'Halo',
        sku: 'HALO-RL6-DM',
      },
      {
        id: 'ri-24',
        optionName: 'Pendant Light Rough-In (Island)',
        description:
          'Electrical rough-in for 3 pendant lights over kitchen island. Fixtures by buyer.',
        category: 'Interior',
        subcategory: 'Lighting & Electrical',
        variantSet: 'Kitchen Lighting',
        price: 350,
        isStandard: false,
        hasImage: false,
        matchSource: 'exact_sku',
        status: 'pending',
        manufacturerName: 'N/A',
      },
      {
        id: 'ri-25',
        optionName: 'USB Outlet Package',
        description:
          'USB-A/C combo outlets in kitchen, office, and primary bedroom. 6 locations total.',
        category: 'Interior',
        subcategory: 'Lighting & Electrical',
        variantSet: 'Electrical',
        price: 280,
        isStandard: false,
        hasImage: false,
        matchSource: 'manual_match',
        status: 'pending',
        manufacturerName: 'Leviton',
      },

      // ── Paint & Finishes ──
      {
        id: 'ri-26',
        optionName: 'Interior Paint — Premium Palette',
        description:
          'Upgrade to premium Sherwin-Williams Emerald paint in curated color palette. 3 colors included.',
        category: 'Interior',
        subcategory: 'Paint & Finishes',
        variantSet: 'Interior Paint',
        price: 1500,
        isStandard: false,
        hasImage: true,
        imageUrl:
          'https://images.unsplash.com/photo-1562259949-e8e7689d7828?auto=format&fit=crop&w=400&q=80',
        matchSource: 'manufacturer_match',
        status: 'pending',
        manufacturerName: 'Sherwin-Williams',
      },
      {
        id: 'ri-27',
        optionName: 'Standard Interior Paint',
        description:
          'Builder-grade flat/eggshell paint in 2 standard neutral tones. Sherwin-Williams ProMar 200.',
        category: 'Interior',
        subcategory: 'Paint & Finishes',
        variantSet: 'Interior Paint',
        price: 0,
        isStandard: true,
        hasImage: false,
        matchSource: 'exact_sku',
        status: 'pending',
        manufacturerName: 'Sherwin-Williams',
        sku: 'SW-PM200-STD',
      },
      {
        id: 'ri-28',
        optionName: 'Accent Wall — Board & Batten',
        description:
          'Board and batten accent wall in primary bedroom or living room. Painted to match trim color.',
        category: 'Interior',
        subcategory: 'Paint & Finishes',
        variantSet: 'Accent Features',
        price: 1800,
        isStandard: false,
        hasImage: true,
        imageUrl:
          'https://images.unsplash.com/photo-1615529328331-f8917597711f?auto=format&fit=crop&w=400&q=80',
        matchSource: 'new_entry',
        status: 'pending',
      },
    ],
  },
]
